'use client';

import React, { createContext, useContext, ReactNode, useState, useCallback, useMemo } from 'react';

type LoadingMap = Record<string, boolean>;

interface LoadingContextType {
  isLoading(featureKey: string): boolean;
  setLoading(featureKey: string, loading: boolean): void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const useLoading = (): LoadingContextType => {
  const ctx = useContext(LoadingContext);
  if (!ctx) throw new Error('useLoading must be inside LoadingProvider');
  return ctx;
};

export const LoadingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loadingMap, setLoadingMap] = useState<LoadingMap>({});

  const setLoading = useCallback((featureKey: string, loading: boolean) => {
    setLoadingMap((prev) => {
      const next = { ...prev };
      if (loading) {
        next[featureKey] = true;
      } else {
        // if loading is false, remove the key entirely
        delete next[featureKey];
      }
      return next;
    });
  }, []);

  const isLoading = useCallback((featureKey: string) => Boolean(loadingMap[featureKey]), [loadingMap]);

  const value = useMemo(() => ({ isLoading, setLoading }), [isLoading, setLoading]);

  return <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>;
};
